<template>
  <transition name="custom-modal-fade">
    <div
      v-if="isVisible"
      class="custom-modal-overlay"
    >
      <div class="custom-modal-card">
        <h1 class="custom-modal-card-title">
          Change Password
        </h1>
        <section class="custom-modal-card-body">
          <form @submit.prevent="changePassword">
            <div class="input-group">
              <label for="oldPassword">Old Password</label>
              <input
                id="oldPassword"
                v-model="oldPassword"
                type="password"
                class="modern-input"
                placeholder="Enter your old password"
                required
              >
            </div>

            <div class="input-group">
              <label for="newPassword">New Password</label>
              <input
                id="newPassword"
                v-model="newPassword"
                type="password"
                class="modern-input"
                placeholder="Enter your new password"
                required
              >
            </div>

            <div class="input-group">
              <label for="confirmNewPassword">Confirm New Password</label>
              <input
                id="confirmNewPassword"
                v-model="confirmNewPassword"
                class="modern-input"
                type="password"
                placeholder="Confirm your new password"
                required
              >
            </div>

            <p
              v-if="message"
              class="error-message"
              :class="messageType === 'is-danger' ? 'is-danger-text' : 'is-success-text'"
            >
              {{ message }}
            </p>
            <div class="modal-buttons-wrapper">
              <button
                type="button"
                class="login-button secondary-button"
                @click="closeModal"
              >
                Close
              </button>
              <button
                type="submit"
                class="login-button"
                :class="{ 'is-loading': isLoading }"
              >
                Change Password
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  </transition>
</template>

<script>
import api from '../services/api';

export default {
  name: 'ChangePasswordModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: '',
      isLoading: false,
      message: '',
      messageType: 'is-info',
    };
  },
  watch: {
    isVisible(newVal) {
      if (!newVal) {
        // Reset form when modal is closed
        this.oldPassword = '';
        this.newPassword = '';
        this.confirmNewPassword = '';
        this.message = '';
        this.messageType = 'is-info';
      }
    },
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    async changePassword() {
      this.message = '';
      this.isLoading = true;

      if (this.newPassword !== this.confirmNewPassword) {
        this.message = 'New password and confirmation do not match.';
        this.messageType = 'is-danger';
        this.isLoading = false;
        return;
      }

      try {
        await api.changePassword({
          oldPassword: this.oldPassword,
          newPassword: this.newPassword,
        });
        this.message = 'Password changed successfully!';
        this.messageType = 'is-success';
        this.closeModal();
        this.$buefy.toast.open({
          message: 'Password changed successfully!',
          type: 'is-success',
          position: 'is-top-right',
        });
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.message) || 'Failed to change password.';
        this.messageType = 'is-danger';
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.custom-modal-card {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 450px;
  margin: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 40px;
  text-align: center; /* Center align for consistency with login */
  position: relative;
}

.custom-modal-card-head {
  display: none; /* Hide default modal header */
}

.custom-modal-card-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 30px;
}

.custom-modal-card-body {
  padding: 0;
  flex-grow: 1;
  overflow-y: auto;
}

.custom-modal-card-foot {
  display: none; /* Hide default modal footer */
}

.input-group {
  margin-bottom: 20px;
  text-align: left;
}

label {
  display: block;
  margin-bottom: 10px;
  font-weight: 600;
  color: #4a5568;
}

.modern-input {
  width: 100%;
  padding: 14px 18px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.modern-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
}

.login-button {
  width: 100%;
  padding: 15px 15px; /* Reduced padding */
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 1rem; /* Reduced font size */
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
  margin-top: 20px; /* Add margin to separate from inputs */
}

.login-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

.modal-buttons-wrapper {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  margin-top: 30px;
}

.secondary-button {
  background: #a0aec0; /* A neutral color for the close button */
  box-shadow: 0 4px 15px rgba(160, 174, 192, 0.3);
}

.secondary-button:hover {
  background: #718096;
  box-shadow: 0 6px 20px rgba(160, 174, 192, 0.4);
}

.error-message {
  color: #ef4444;
  text-align: center;
  margin-top: 20px;
  font-weight: 500;
  font-size: 0.95rem;
}

.is-danger-text {
  color: #ef4444;
}

.is-success-text {
  color: #008000;
}

.delete {
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 2.2rem; /* Larger close button */
  height: 30px; /* Adjusted size */
  width: 30px; /* Adjusted size */
  color: #888; /* Color for the X */
  z-index: 10; /* Ensure it's above other content */
  transition: color 0.2s ease;
}

.delete:hover {
  color: #555;
}

.delete::before, .delete::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 2px;
  height: 100%;
  background-color: currentColor; /* Use current color for the lines */
}

.delete::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.delete::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

/* Transitions */
.custom-modal-fade-enter-active, .custom-modal-fade-leave-active {
  transition: all 0.3s ease;
}

.custom-modal-fade-enter, .custom-modal-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.custom-modal-fade-enter-active .custom-modal-card,
.custom-modal-fade-leave-active .custom-modal-card {
  transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.custom-modal-fade-enter .custom-modal-card,
.custom-modal-fade-leave-to .custom-modal-card {
  transform: scale(0.9) translateY(-20px);
}
</style>
